import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Row, Col, Card, Form, Input, Select, DatePicker, Button, Checkbox, List, Modal, Popconfirm, notification, Typography, Spin, Tag, Space, Descriptions } from 'antd';
import { PlayCircleOutlined, CheckOutlined, StopOutlined, ReloadOutlined, SaveOutlined, PlusOutlined, DeleteOutlined, SendOutlined } from '@ant-design/icons';
import moment from 'moment';

import { useAuth } from '../../authentication/AuthProvider';
import request from '../../lib/request';
import { AttachmentsPanel } from '../../components/AttachmentsPanel';
import { TASK_STATUS_COLORS, PRIORITY_COLORS, DueDate } from './TasksPage';

const toDate = (v) => (v ? moment(v) : null);
const when = (v) => (v ? moment(v).format('YYYY-MM-DD HH:mm') : '-');

/** One Task: status actions, details, checklist, comments, dependencies and files. */
export default function TaskDetailPage() {
  const { id } = useParams();
  const auth = useAuth();
  const [task, setTask] = React.useState(null);
  const [users, setUsers] = React.useState([]);
  const [saving, setSaving] = React.useState(false);
  const [newItem, setNewItem] = React.useState('');
  const [comment, setComment] = React.useState('');
  const [form] = Form.useForm();

  const apply = (r) => {
    if (r.status) {
      setTask(r.data);
      form.setFieldsValue({ ...r.data, exp_start_date: toDate(r.data.exp_start_date), exp_end_date: toDate(r.data.exp_end_date) });
    } else {
      notification.error({ message: r.message });
    }
    return r;
  };

  const fetchTask = React.useCallback(() => {
    request.get(`/tasks/${id}`).then((r) => r.json()).then(apply);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  React.useEffect(() => {
    fetchTask();
    request.get('/users', auth.user.companyId ? { company_id: auth.user.companyId } : {}).then((r) => r.json()).then((r) => r.status && setUsers(r.data));
  }, [fetchTask, auth.user.companyId]);

  const call = (method, path, body) => request[method](path, body).then((r) => r.json()).then(apply);

  const setStatus = (status, force) => {
    request
      .post(`/tasks/${id}/status`, { status, force: !!force })
      .then((r) => r.json())
      .then((r) => {
        if (r.status) {
          apply(r);
          notification.success({ message: `Task ${status.toLowerCase()}` });
        } else if (r.data?.open_checklist_items) {
          Modal.confirm({
            title: 'Checklist not finished',
            content: r.message,
            okText: 'Complete anyway',
            onOk: () => setStatus(status, true),
          });
        } else {
          notification.warning({ message: r.message });
        }
      });
  };

  const handleSave = () => {
    form.validateFields().then((values) => {
      setSaving(true);
      call('put', `/tasks/${id}`, {
        subject: values.subject,
        description: values.description,
        priority: values.priority,
        assigned_user_id: values.assigned_user_id,
        exp_start_date: values.exp_start_date ? values.exp_start_date.format('YYYY-MM-DD') : null,
        exp_end_date: values.exp_end_date ? values.exp_end_date.format('YYYY-MM-DD') : null,
      })
        .then((r) => r.status && notification.success({ message: 'Task saved' }))
        .finally(() => setSaving(false));
    });
  };

  const addItem = () => {
    if (!newItem.trim()) return;
    call('post', `/tasks/${id}/checklist`, { title: newItem.trim() }).then((r) => r.status && setNewItem(''));
  };
  const addComment = () => {
    if (!comment.trim()) return;
    call('post', `/tasks/${id}/comments`, { body: comment.trim() }).then((r) => r.status && setComment(''));
  };

  if (!task) return <Spin style={{ marginTop: 40 }} />;
  const closed = task.status === 'COMPLETED' || task.status === 'CANCELLED';
  const items = task.TaskChecklistItems || [];
  const done = items.filter((i) => i.is_done).length;
  const isManager = auth.user.role === 'SYSTEM_ADMIN' || auth.user.role === 'COMPANY_MANAGER';

  return (
    <div>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }} align="start">
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {task.subject} <Tag color={TASK_STATUS_COLORS[task.status]}>{task.status}</Tag> <Tag color={PRIORITY_COLORS[task.priority]}>{task.priority}</Tag>
          </Typography.Title>
          <Typography.Text type="secondary">
            <Link to="/tasks">Tasks</Link> / <Link to="/projects">{task.Project?.name}</Link>
            {task.Project?.Client && (
              <span>
                {' '}
                / <Link to={`/clients/${task.Project.Client.id}`}>{task.Project.Client.client_name}</Link>
              </span>
            )}
          </Typography.Text>
        </div>
        <Space>
          {task.status === 'OPEN' && (
            <Button icon={<PlayCircleOutlined />} onClick={() => setStatus('WORKING')}>
              Start
            </Button>
          )}
          {!closed && (
            <Button type="primary" icon={<CheckOutlined />} onClick={() => setStatus('COMPLETED')}>
              Complete
            </Button>
          )}
          {!closed && (
            <Popconfirm title="Cancel this task?" onConfirm={() => setStatus('CANCELLED')}>
              <Button danger icon={<StopOutlined />}>
                Cancel
              </Button>
            </Popconfirm>
          )}
          {closed && (
            <Button icon={<ReloadOutlined />} onClick={() => setStatus('OPEN')}>
              Reopen
            </Button>
          )}
        </Space>
      </Space>

      <Row gutter={16}>
        <Col xs={24} lg={14}>
          <Card title="Details" size="small" style={{ marginBottom: 16 }}>
            <Form form={form} layout="vertical" disabled={closed}>
              <Form.Item name="subject" label="Subject" rules={[{ required: true }]}>
                <Input maxLength={255} />
              </Form.Item>
              <Form.Item name="description" label="Description">
                <Input.TextArea rows={3} />
              </Form.Item>
              <Space style={{ display: 'flex' }} align="start" wrap>
                <Form.Item name="assigned_user_id" label="Assigned to" style={{ width: 220 }}>
                  <Select allowClear showSearch optionFilterProp="label" options={users.map((u) => ({ value: u.id, label: u.name }))} />
                </Form.Item>
                <Form.Item name="priority" label="Priority" style={{ width: 120 }}>
                  <Select options={['LOW', 'MEDIUM', 'HIGH'].map((v) => ({ value: v, label: v }))} />
                </Form.Item>
                <Form.Item name="exp_start_date" label="Start" style={{ width: 150 }}>
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="exp_end_date" label="Due" style={{ width: 150 }}>
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Space>
              {!closed && (
                <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
                  Save
                </Button>
              )}
            </Form>
          </Card>

          <Card title={`Checklist (${done}/${items.length})`} size="small" style={{ marginBottom: 16 }}>
            <List
              size="small"
              dataSource={items}
              locale={{ emptyText: 'No checklist items - add the steps this task needs' }}
              renderItem={(item) => (
                <List.Item
                  actions={[
                    <Popconfirm key="del" title="Remove this item?" onConfirm={() => call('delete', `/tasks/${id}/checklist/${item.id}`)}>
                      <Button type="text" size="small" danger icon={<DeleteOutlined />} disabled={closed} />
                    </Popconfirm>,
                  ]}
                >
                  <Checkbox checked={item.is_done} disabled={closed} onChange={(e) => call('put', `/tasks/${id}/checklist/${item.id}`, { is_done: e.target.checked })}>
                    <span style={{ textDecoration: item.is_done ? 'line-through' : 'none' }}>{item.title}</span>
                    {item.is_done && item.DoneBy && (
                      <Typography.Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                        {item.DoneBy.name}, {when(item.done_at)}
                      </Typography.Text>
                    )}
                  </Checkbox>
                </List.Item>
              )}
            />
            {!closed && (
              <Space.Compact style={{ width: '100%', marginTop: 8 }}>
                <Input placeholder="Add checklist item" value={newItem} onChange={(e) => setNewItem(e.target.value)} onPressEnter={addItem} maxLength={255} />
                <Button icon={<PlusOutlined />} onClick={addItem}>
                  Add
                </Button>
              </Space.Compact>
            )}
          </Card>

          <Card title={`Comments (${(task.TaskComments || []).length})`} size="small" style={{ marginBottom: 16 }}>
            <List
              size="small"
              dataSource={task.TaskComments || []}
              locale={{ emptyText: 'No comments yet' }}
              renderItem={(c) => (
                <List.Item
                  actions={
                    c.user_id === auth.user.id || isManager
                      ? [
                          <Popconfirm key="del" title="Remove this comment?" onConfirm={() => call('delete', `/tasks/${id}/comments/${c.id}`)}>
                            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                          </Popconfirm>,
                        ]
                      : []
                  }
                >
                  <List.Item.Meta title={<span>{c.User?.name || 'Unknown'} <Typography.Text type="secondary" style={{ fontWeight: 400, fontSize: 12 }}>{when(c.createdAt)}</Typography.Text></span>} description={<div style={{ whiteSpace: 'pre-wrap', color: 'inherit' }}>{c.body}</div>} />
                </List.Item>
              )}
            />
            <div style={{ marginTop: 8 }}>
              <Input.TextArea rows={2} placeholder="Write a comment" value={comment} onChange={(e) => setComment(e.target.value)} maxLength={5000} />
              <Button icon={<SendOutlined />} style={{ marginTop: 8 }} onClick={addComment} disabled={!comment.trim()}>
                Add comment
              </Button>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card title="Timing" size="small" style={{ marginBottom: 16 }}>
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="Due">
                <DueDate task={task} />
              </Descriptions.Item>
              <Descriptions.Item label="Started">{when(task.started_at)}</Descriptions.Item>
              <Descriptions.Item label="Completed">{task.completed_at ? `${when(task.completed_at)}${task.CompletedBy ? ` by ${task.CompletedBy.name}` : ''}` : '-'}</Descriptions.Item>
              <Descriptions.Item label="Created">{when(task.createdAt)}</Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Dependencies" size="small" style={{ marginBottom: 16 }}>
            <Typography.Text strong>Waits for</Typography.Text>
            <List
              size="small"
              dataSource={(task.TaskDependencies || []).map((d) => d.DependsOnTask).filter(Boolean)}
              locale={{ emptyText: 'Nothing - can start any time' }}
              renderItem={(t) => (
                <List.Item>
                  <Link to={`/tasks/${t.id}`}>{t.subject}</Link> <Tag color={TASK_STATUS_COLORS[t.status]}>{t.status}</Tag>
                </List.Item>
              )}
            />
            <Typography.Text strong>Blocks</Typography.Text>
            <List
              size="small"
              dataSource={(task.DependedOnBy || []).map((d) => d.Task).filter(Boolean)}
              locale={{ emptyText: 'Nothing waits on this task' }}
              renderItem={(t) => (
                <List.Item>
                  <Link to={`/tasks/${t.id}`}>{t.subject}</Link> <Tag color={TASK_STATUS_COLORS[t.status]}>{t.status}</Tag>
                </List.Item>
              )}
            />
          </Card>

          <Card title="Files" size="small">
            <AttachmentsPanel ownerType="TASK" ownerId={+id} categories={['GENERAL', 'SUPPORTING_DOCUMENT', 'CORRESPONDENCE']} />
          </Card>
        </Col>
      </Row>
    </div>
  );
}
